<script setup>
import { computed, reactive } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { isPhoneE164OrEmpty } from 'shared/helpers/Validators';
import { useMapGetter } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';

const WUZAPI_API_BASE_URL = 'https://zap.skyaigroup.com';

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const uiFlags = useMapGetter('inboxes/getUIFlags');

const form = reactive({
  inboxName: '',
  phoneNumber: '',
  apiBaseUrl: WUZAPI_API_BASE_URL,
});

const rules = computed(() => ({
  inboxName: { required },
  phoneNumber: { required, isPhoneE164OrEmpty },
  apiBaseUrl: { required },
}));

const v$ = useVuelidate(rules, form);

const createChannel = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  try {
    const whatsappChannel = await store.dispatch('inboxes/createChannel', {
      name: form.inboxName.trim(),
      channel: {
        type: 'whatsapp',
        phone_number: form.phoneNumber,
        provider: 'wuzapi',
        provider_config: {
          api_base_url: form.apiBaseUrl.trim(),
        },
      },
    });

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: {
        page: 'new',
        inbox_id: whatsappChannel.id,
      },
    });
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <form class="flex flex-col flex-wrap mx-0" @submit.prevent="createChannel()">
    <div class="flex-grow-0 flex-shrink-0">
      <label :class="{ error: v$.inboxName.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.LABEL') }}
        <input
          v-model="form.inboxName"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.PLACEHOLDER')"
          @blur="v$.inboxName.$touch"
        />
        <span v-if="v$.inboxName.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.ERROR') }}
        </span>
      </label>
    </div>

    <div class="flex-grow-0 flex-shrink-0">
      <label :class="{ error: v$.phoneNumber.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.LABEL') }}
        <input
          v-model="form.phoneNumber"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.PLACEHOLDER')"
          @blur="v$.phoneNumber.$touch"
        />
        <span v-if="v$.phoneNumber.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.ERROR') }}
        </span>
      </label>
    </div>

    <div class="flex-grow-0 flex-shrink-0">
      <label :class="{ error: v$.apiBaseUrl.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.WUZAPI_API_BASE_URL.LABEL') }}
        <input
          v-model="form.apiBaseUrl"
          type="text"
          :placeholder="
            $t('INBOX_MGMT.ADD.WHATSAPP.WUZAPI_API_BASE_URL.PLACEHOLDER')
          "
          @blur="v$.apiBaseUrl.$touch"
        />
        <span v-if="v$.apiBaseUrl.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.WUZAPI_API_BASE_URL.ERROR') }}
        </span>
      </label>
    </div>

    <div class="w-full mt-4">
      <NextButton
        type="submit"
        solid
        blue
        :is-loading="uiFlags.isCreating"
        :label="$t('INBOX_MGMT.ADD.WHATSAPP.SUBMIT_BUTTON')"
      />
    </div>
  </form>
</template>
